import {useMemo} from 'react'
import {useQuery} from 'react-query'
import {axiosClientAuth} from '../utils/axiosClient'
import { REGULATORY_OPERATOR_KEY } from '../../config/countryConfig'

export type OperatorVerdictStat = {
  operator: string
  whitelisted: number
  blacklisted: number
  pending?: number
}

type ResourceAnalyticsApiResponse = {
  status?: boolean
  message?: string
  data?: {
    ip?: {
      total?: number
      by_operator?: Record<
        string,
        {
          whitelisted?: number
          blacklisted?: number
          pending?: number
        }
      >
    }
  }
}

export const RESOURCE_ANALYTICS_CACHE_KEY = 'analytics-resources'
const RESOURCE_ANALYTICS_STALE_TIME = 5 * 60 * 1000
const RESOURCE_ANALYTICS_CACHE_TIME = 10 * 60 * 1000

const fetchResourceAnalytics = async (): Promise<ResourceAnalyticsApiResponse> => {
  const response = await axiosClientAuth.get('/analytics/resources/')
  return response.data || {}
}

const mapOperatorName = (operatorKey: string) => {
  return operatorKey.toUpperCase()
}

export const useResourceAnalytics = () => {
  const query = useQuery(RESOURCE_ANALYTICS_CACHE_KEY, fetchResourceAnalytics, {
    staleTime: RESOURCE_ANALYTICS_STALE_TIME,
    cacheTime: RESOURCE_ANALYTICS_CACHE_TIME,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const mappedData = useMemo(() => {
    const ipData = query.data?.data?.ip
    const byOperator = ipData?.by_operator || {}

    const ipVerdictStats: OperatorVerdictStat[] = Object.entries(byOperator)
      .filter(([operatorKey]) => operatorKey !== 'self' && operatorKey !== REGULATORY_OPERATOR_KEY)
      .map(([operatorKey, operatorStats]) => ({
        operator: mapOperatorName(operatorKey),
        whitelisted: operatorStats?.whitelisted || 0,
        blacklisted: operatorStats?.blacklisted || 0,
        pending: operatorStats?.pending || 0,
      }))

    const selfStats = byOperator['self'] || {}

    return {
      ipVerdictStats,
      ipVerdictTotal: ipData?.total || 0,
      ipSelfStats: {
        whitelisted: selfStats?.whitelisted || 0,
        blacklisted: selfStats?.blacklisted || 0,
      },
    }
  }, [query.data])

  return {
    ...query,
    ...mappedData,
  }
}
